'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Bell,
  CheckCheck,
  ChevronDown,
  Filter,
  Trash2,
  XCircle,
} from 'lucide-react';
import { useNotifications } from '@/hooks/useNotifications';
import { useTranslation } from '@/components/i18n-provider';
import { NotificationItem } from '@/components/notification-item';
import { Spinner } from '@/components/ui/spinner';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

/**
 * Self-contained notifications list. Used by both the standalone
 * `/notifications` page and the workspace's top edge panel so we don't
 * have two implementations to keep in sync.
 *
 * Rendered without any outer chrome — callers wrap it in their own
 * container (page layout vs. floating glass panel). Density and the
 * presence of the title can be toggled via props.
 */
export function NotificationsView({
  showTitle = true,
  dense = false,
  className,
}: {
  /** Render the "Notifications" header row (title + bulk actions). */
  showTitle?: boolean;
  /**
   * Narrow-container variant (the workspace panel ~340px). Bulk actions
   * collapse to icon-only — the `sm:` label breakpoint keys off the
   * VIEWPORT, not the panel, so on a wide screen a narrow panel would
   * otherwise show all the labels and overflow.
   */
  dense?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  const {
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    markRead,
    markAllRead,
    performAction,
    performCtaAction,
    deleteNotification,
  } = useNotifications();
  const [filter, setFilter] = useState('');
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState<'read' | 'all' | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const FILTER_TABS = [
    { value: '', label: t('notifications.filterAll') },
    { value: 'payment_required', label: t('notifications.filterPayments') },
    { value: 'message_summary', label: t('notifications.filterMessages') },
    { value: 'mention', label: t('notifications.filterMentions') },
    { value: 'reply', label: t('notifications.filterReplies') },
    { value: 'content_shared', label: t('notifications.filterShared') },
    { value: 'system', label: t('notifications.filterSystem') },
  ] as const;

  const filtered = filter
    ? notifications.filter((n) => n.type === filter)
    : notifications;

  const handleLoadMore = useCallback(async () => {
    if (!hasMore || loadingMore) return;
    setLoadingMore(true);
    const lastItem = filtered[filtered.length - 1];
    const items = await fetchNotifications({
      cursor: lastItem?.id,
      type: filter || undefined,
    });
    if (items.length < 20) setHasMore(false);
    setLoadingMore(false);
  }, [hasMore, loadingMore, filtered, fetchNotifications, filter]);

  // Infinite scroll: an IntersectionObserver watches the sentinel below
  // the last row and pulls the next page in as soon as the user scrolls
  // close to the bottom. The 240px bottom rootMargin gives the network
  // round-trip a head start so the panel never feels like it's stuck.
  useEffect(() => {
    const root = scrollRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel || !hasMore) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !loadingMore && hasMore) {
            void handleLoadMore();
            return;
          }
        }
      },
      { root, rootMargin: '0px 0px 240px 0px', threshold: 0 },
    );
    io.observe(sentinel);
    return () => io.disconnect();
  }, [hasMore, loadingMore, handleLoadMore, filter]);

  const handleMute = useCallback(async (sourceType: string, sourceId: string) => {
    try {
      await fetch('/api/notifications/mutes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sourceType, sourceId }),
      });
    } catch {
      /* ignore — best-effort */
    }
  }, []);

  const handleDelete = useCallback(
    async (action: 'delete_read' | 'delete_all') => {
      try {
        await fetch('/api/notifications', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action }),
        });
        void fetchNotifications();
      } catch {
        /* ignore */
      }
      setDeleteConfirm(null);
    },
    [fetchNotifications],
  );

  const currentFilterLabel =
    FILTER_TABS.find((tab) => tab.value === filter)?.label ?? FILTER_TABS[0].label;

  return (
    <div className={cn('flex h-full flex-col', className)}>
      {showTitle && (
        <div className="flex items-center gap-2 px-4 pt-4">
          <Bell className="h-5 w-5 text-foreground shrink-0" />
          <h2 className="text-lg font-semibold">{t('notifications.pageTitle')}</h2>
          {unreadCount > 0 && (
            <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </div>
      )}

      {/*
        Single toolbar row: filter dropdown on the left, bulk actions
        right-aligned. Replaces the previous pill-tab strip + separate
        header action cluster so both axes are scannable in one glance
        and the layout collapses cleanly on narrow viewports.
      */}
      <div className="flex items-center gap-2 px-4 py-2">
        <div className="mr-auto flex items-center gap-1">
          {unreadCount > 0 && deleteConfirm === null && (
            <button
              type="button"
              onClick={markAllRead}
              // `dense` hides the text span, so the name must not depend on
              // content — the explicit label holds in both modes.
              aria-label={t('notifications.markAllRead')}
              title={t('notifications.markAllRead')}
              className="text-xs text-muted-foreground hover:text-foreground hover:bg-muted/60 rounded-full flex items-center gap-1 px-2 py-1 transition-colors"
            >
              <CheckCheck className="h-3.5 w-3.5" />
              <span className={dense ? 'hidden' : 'hidden sm:inline'}>
                {t('notifications.markAllRead')}
              </span>
            </button>
          )}
          <NotificationsHeaderActions
            t={t}
            dense={dense}
            deleteConfirm={deleteConfirm}
            setDeleteConfirm={setDeleteConfirm}
            onConfirm={handleDelete}
            hasAny={notifications.length > 0}
          />
        </div>

        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-muted/50 text-foreground hover:bg-muted transition-colors min-w-0"
            >
              <Filter className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{currentFilterLabel}</span>
              <ChevronDown className="h-3 w-3 shrink-0 opacity-60" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            sideOffset={6}
            className="min-w-[180px] p-1"
          >
            <div className="flex flex-col">
              {FILTER_TABS.map((tab) => (
                <button
                  key={tab.value}
                  onClick={() => {
                    setFilter(tab.value);
                    setHasMore(true);
                  }}
                  className={cn(
                    'text-left text-sm px-3 py-1.5 rounded-md transition-colors',
                    filter === tab.value
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-muted',
                  )}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <div
        ref={scrollRef}
        className="ws-selectable flex-1 min-h-0 overflow-y-auto px-2 pb-4"
      >
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Spinner size="lg" tone="muted" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center">
            <Bell className="h-8 w-8 mx-auto text-muted-foreground/30 mb-2" />
            <p className="text-sm text-muted-foreground">
              {filter ? t('notifications.noneOfType') : t('notifications.none')}
            </p>
          </div>
        ) : (
          <div className="space-y-0.5">
            {filtered.map((n) => (
              <NotificationItem
                key={n.id}
                notification={n}
                onMarkRead={markRead}
                onAction={performAction}
                onCtaAction={performCtaAction}
                onDelete={deleteNotification}
                onMute={handleMute}
              />
            ))}
          </div>
        )}

        {/*
          Pagination sentinel: always rendered (when there are rows) so
          the IntersectionObserver has a stable target. The effect
          guards on `hasMore` and `loadingMore` to avoid duplicate
          fetches; once the server returns < 20 rows, we stop observing.
        */}
        {filtered.length > 0 && hasMore && (
          <div ref={sentinelRef} aria-hidden className="h-4" />
        )}
        {loadingMore && (
          <div className="flex justify-center pt-3 pb-1">
            <Spinner size="sm" tone="muted" />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Header bulk-action cluster. Two destructive actions:
 *  - "Clear read"   → deletes only the already-read notifications.
 *  - "Dismiss all"  → deletes everything, read or unread.
 *
 * Both share a single inline confirm state. Tapping a button arms it
 * (shows Confirm / Cancel); a second tap confirms. We track which
 * action is armed via `deleteConfirm: 'read' | 'all' | null` so the two
 * buttons can't both be in confirm mode at once.
 */
function NotificationsHeaderActions({
  t,
  dense = false,
  deleteConfirm,
  setDeleteConfirm,
  onConfirm,
  hasAny,
}: {
  t: (key: string) => string;
  dense?: boolean;
  deleteConfirm: 'read' | 'all' | null;
  setDeleteConfirm: (next: 'read' | 'all' | null) => void;
  onConfirm: (action: 'delete_read' | 'delete_all') => void;
  hasAny: boolean;
}) {
  if (deleteConfirm) {
    return (
      <div className="flex items-center gap-1">
        <span className="text-[11px] text-muted-foreground hidden md:inline">
          {deleteConfirm === 'all'
            ? t('notifications.confirmDismissAll')
            : t('notifications.confirmClear')}
        </span>
        <button
          onClick={() =>
            onConfirm(deleteConfirm === 'all' ? 'delete_all' : 'delete_read')
          }
          className="text-[11px] px-2 py-0.5 rounded bg-red-600 text-white hover:bg-red-700 transition-colors"
        >
          {t('notifications.confirmClear')}
        </button>
        <button
          onClick={() => setDeleteConfirm(null)}
          className="text-[11px] px-2 py-0.5 rounded text-muted-foreground hover:bg-accent transition-colors"
        >
          {t('actions.cancel')}
        </button>
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setDeleteConfirm('read')}
        aria-label={t('notifications.clearRead')}
        title={t('notifications.clearRead')}
        className="text-xs text-muted-foreground hover:text-foreground hover:bg-muted/60 rounded-full flex items-center gap-1 px-2 py-1 transition-colors"
      >
        <Trash2 className="h-3.5 w-3.5" />
        <span className={dense ? 'hidden' : 'hidden sm:inline'}>{t('notifications.clearRead')}</span>
      </button>
      {hasAny && (
        <button
          type="button"
          onClick={() => setDeleteConfirm('all')}
          aria-label={t('notifications.dismissAll')}
          title={t('notifications.dismissAll')}
          className="text-xs text-muted-foreground hover:text-red-500 hover:bg-red-500/10 rounded-full flex items-center gap-1 px-2 py-1 transition-colors"
        >
          <XCircle className="h-3.5 w-3.5" />
          <span className={dense ? 'hidden' : 'hidden sm:inline'}>
            {t('notifications.dismissAll')}
          </span>
        </button>
      )}
    </>
  );
}
